import React, { useState, useEffect } from 'react';
import { IconCalendar, IconX, IconVideo, IconPhone, IconBuilding } from '../Icons';

export interface EventPayload {
  title: string;
  description: string;
  date: string;
  time: string;
  meetingType: 'video' | 'audio' | 'in_person';
  locationOrLink: string;
}

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (eventData: EventPayload) => Promise<void> | void;
}

export const EventModal: React.FC<EventModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [meetingType, setMeetingType] = useState<'video' | 'audio' | 'in_person'>('video');
  const [dateOption, setDateOption] = useState<'today' | 'tomorrow' | 'next_monday' | 'custom'>('today');
  const [customDate, setCustomDate] = useState('');
  const [time, setTime] = useState('10:00 AM');
  const [locationOrLink, setLocationOrLink] = useState('Videollamada Zoik');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setMeetingType('video');
      setDateOption('today');
      setCustomDate('');
      setTime('10:00 AM');
      setLocationOrLink('Videollamada Zoik');
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleTypeChange = (type: 'video' | 'audio' | 'in_person') => {
    setMeetingType(type);
    if (type === 'video') setLocationOrLink('Videollamada Zoik');
    else if (type === 'audio') setLocationOrLink('Llamada Zoik');
    else setLocationOrLink('Oficina Principal');
  };

  const getComputedDateString = () => {
    const d = new Date();
    if (dateOption === 'tomorrow') {
      d.setDate(d.getDate() + 1);
    } else if (dateOption === 'next_monday') {
      const day = d.getDay();
      const diff = (8 - day) % 7 || 7;
      d.setDate(d.getDate() + diff);
    } else if (dateOption === 'custom' && customDate) {
      const [year, month, day] = customDate.split('-');
      if (year && month && day) {
        const customD = new Date(Number(year), Number(month) - 1, Number(day));
        const dayStr = String(customD.getDate()).padStart(2, '0');
        const monthStr = customD.toLocaleString('es-ES', { month: 'short' }).toUpperCase().replace('.', '');
        return `${dayStr} ${monthStr} ${customD.getFullYear()}`;
      }
    }
    const dayStr = String(d.getDate()).padStart(2, '0');
    const monthStr = d.toLocaleString('es-ES', { month: 'short' }).toUpperCase().replace('.', '');
    return `${dayStr} ${monthStr} ${d.getFullYear()}`;
  };

  const canSubmit = title.trim().length > 0 && !submitting;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const finalDate = getComputedDateString();

      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        date: finalDate,
        time: time.trim() || '10:00 AM',
        meetingType,
        locationOrLink: locationOrLink.trim() || 'Videollamada Zoik'
      });

      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '20px'
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '24px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(15, 42, 74, 0.28)',
          border: '1.5px solid rgba(226, 232, 240, 0.95)',
          overflow: 'hidden',
          animation: 'zoikDrawerSlideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px 16px',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
              }}
            >
              <IconCalendar size={22} color="#ffffff" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 900, color: '#0f172a' }}>
                Programar Reunión / Evento
              </h3>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                Coordina una sesión y compártela en el chat
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: 'none',
              background: '#f1f5f9',
              color: '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#e2e8f0';
              e.currentTarget.style.color = '#0f172a';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <IconX size={16} />
          </button>
        </div>

        {/* Scrollable Body */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div
            style={{
              padding: '20px 24px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px'
            }}
          >
            {/* Título de la reunión */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#475569', letterSpacing: '0.6px' }}>
                  TÍTULO DEL EVENTO O REUNIÓN
                </label>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>{title.length}/80</span>
              </div>
              <input
                type="text"
                maxLength={80}
                placeholder="Ej. Revisión de Avances y Próximos Pasos"
                value={title}
                onChange={e => setTitle(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '12px 14px',
                  borderRadius: '14px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#0f172a',
                  outline: 'none',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#0070f3';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 112, 243, 0.15)';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Tipo de Reunión */}
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#475569', letterSpacing: '0.6px', marginBottom: '8px' }}>
                MODALIDAD DE REUNIÓN
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {/* Videollamada */}
                <button
                  type="button"
                  onClick={() => handleTypeChange('video')}
                  style={{
                    padding: '10px 8px',
                    borderRadius: '14px',
                    border: meetingType === 'video' ? '1.5px solid #0070f3' : '1.5px solid #e2e8f0',
                    background: meetingType === 'video' ? 'rgba(0, 112, 243, 0.1)' : '#f8fafc',
                    color: meetingType === 'video' ? '#0070f3' : '#475569',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <IconVideo size={18} color={meetingType === 'video' ? '#0070f3' : '#64748b'} />
                  <span style={{ fontSize: '12px', fontWeight: 800 }}>Videollamada</span>
                </button>

                {/* Voz */}
                <button
                  type="button"
                  onClick={() => handleTypeChange('audio')}
                  style={{
                    padding: '10px 8px',
                    borderRadius: '14px',
                    border: meetingType === 'audio' ? '1.5px solid #10b981' : '1.5px solid #e2e8f0',
                    background: meetingType === 'audio' ? 'rgba(16, 185, 129, 0.1)' : '#f8fafc',
                    color: meetingType === 'audio' ? '#10b981' : '#475569',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <IconPhone size={18} color={meetingType === 'audio' ? '#10b981' : '#64748b'} />
                  <span style={{ fontSize: '12px', fontWeight: 800 }}>Llamada</span>
                </button>

                {/* Presencial */}
                <button
                  type="button"
                  onClick={() => handleTypeChange('in_person')}
                  style={{
                    padding: '10px 8px',
                    borderRadius: '14px',
                    border: meetingType === 'in_person' ? '1.5px solid #f59e0b' : '1.5px solid #e2e8f0',
                    background: meetingType === 'in_person' ? 'rgba(245, 158, 11, 0.1)' : '#f8fafc',
                    color: meetingType === 'in_person' ? '#f59e0b' : '#475569',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <IconBuilding size={18} color={meetingType === 'in_person' ? '#f59e0b' : '#64748b'} />
                  <span style={{ fontSize: '12px', fontWeight: 800 }}>Presencial</span>
                </button>
              </div>
            </div>

            {/* Fecha */}
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#475569', letterSpacing: '0.6px', marginBottom: '8px' }}>
                FECHA PROGRAMADA
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', marginBottom: dateOption === 'custom' ? '8px' : '0' }}>
                <button
                  type="button"
                  onClick={() => setDateOption('today')}
                  style={{
                    padding: '8px 4px',
                    borderRadius: '10px',
                    border: dateOption === 'today' ? '1.5px solid #0070f3' : '1.5px solid #e2e8f0',
                    background: dateOption === 'today' ? 'rgba(0, 112, 243, 0.1)' : '#f8fafc',
                    color: dateOption === 'today' ? '#0070f3' : '#475569',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Hoy
                </button>
                <button
                  type="button"
                  onClick={() => setDateOption('tomorrow')}
                  style={{
                    padding: '8px 4px',
                    borderRadius: '10px',
                    border: dateOption === 'tomorrow' ? '1.5px solid #0070f3' : '1.5px solid #e2e8f0',
                    background: dateOption === 'tomorrow' ? 'rgba(0, 112, 243, 0.1)' : '#f8fafc',
                    color: dateOption === 'tomorrow' ? '#0070f3' : '#475569',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Mañana
                </button>
                <button
                  type="button"
                  onClick={() => setDateOption('next_monday')}
                  style={{
                    padding: '8px 4px',
                    borderRadius: '10px',
                    border: dateOption === 'next_monday' ? '1.5px solid #0070f3' : '1.5px solid #e2e8f0',
                    background: dateOption === 'next_monday' ? 'rgba(0, 112, 243, 0.1)' : '#f8fafc',
                    color: dateOption === 'next_monday' ? '#0070f3' : '#475569',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Próx. Lunes
                </button>
                <button
                  type="button"
                  onClick={() => setDateOption('custom')}
                  style={{
                    padding: '8px 4px',
                    borderRadius: '10px',
                    border: dateOption === 'custom' ? '1.5px solid #0070f3' : '1.5px solid #e2e8f0',
                    background: dateOption === 'custom' ? 'rgba(0, 112, 243, 0.1)' : '#f8fafc',
                    color: dateOption === 'custom' ? '#0070f3' : '#475569',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Otra Fecha
                </button>
              </div>

              {dateOption === 'custom' && (
                <input
                  type="date"
                  value={customDate}
                  onChange={e => setCustomDate(e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#0f172a',
                    outline: 'none',
                    fontFamily: 'inherit',
                    marginTop: '8px'
                  }}
                />
              )}
            </div>

            {/* Hora y Enlace/Lugar */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#475569', letterSpacing: '0.6px', marginBottom: '6px' }}>
                  HORA
                </label>
                <input
                  type="text"
                  placeholder="10:00 AM"
                  value={time}
                  onChange={e => setTime(e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '13.5px',
                    fontWeight: 600,
                    color: '#0f172a',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#475569', letterSpacing: '0.6px', marginBottom: '6px' }}>
                  LUGAR / ENLACE
                </label>
                <input
                  type="text"
                  placeholder="Zoik Meet Room"
                  value={locationOrLink}
                  onChange={e => setLocationOrLink(e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '13.5px',
                    fontWeight: 600,
                    color: '#0f172a',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>

            {/* Detalles / Agenda */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#475569', letterSpacing: '0.6px' }}>
                  DETALLES / AGENDA (OPCIONAL)
                </label>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>{description.length}/200</span>
              </div>
              <textarea
                rows={2}
                maxLength={200}
                placeholder="Añade temas a tratar o notas importantes..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 14px',
                  borderRadius: '14px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#0f172a',
                  outline: 'none',
                  resize: 'none',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              background: '#ffffff',
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: '10px 18px',
                borderRadius: '12px',
                border: '1.5px solid #cbd5e1',
                background: '#ffffff',
                color: '#475569',
                fontWeight: 700,
                fontSize: '13px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                padding: '10px 22px',
                borderRadius: '12px',
                border: 'none',
                background: canSubmit ? 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)' : '#cbd5e1',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '13px',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                boxShadow: canSubmit ? '0 4px 14px rgba(0, 112, 243, 0.35)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.15s ease'
              }}
            >
              {submitting ? (
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid #ffffff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'zoikSpin 0.8s linear infinite'
                    }}
                  />
                  Programando...
                </>
              ) : (
                <>
                  <IconCalendar size={16} color="#ffffff" />
                  Compartir Reunión en el Chat
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
